import { useSyncExternalStore } from 'react';

import { WINDOW_MEDIA } from '../config/windowClasses';

/**
 * WHICH WINDOW CLASS IS THIS? (docs/platform-standards.md §18, KPR-1759)
 *
 *   const { windowClass, compact, medium, expanded, short } = useWindowClass();
 *
 * The only layout signal a component reads. compact is under 600 wide, medium
 * 600 to 1023, expanded 1024 and up; `short` says the window is under 500 tall
 * (a fact beside the class, not a fourth one). No component reads antd's
 * `Grid.useBreakpoint()` to decide a layout: the window-class gate
 * (src/__tests__/windowClassSurfaces.test.js) fails on a new one.
 *
 * It answers from the SAME media queries the stylesheet writes
 * (`WINDOW_MEDIA`, src/config/windowClasses.js): compact is the compact query
 * matching, expanded the expanded one, medium neither. So a component and its
 * CSS switch on the same pixel, zoom and scrollbars included.
 *
 * One store for the whole app: the queries are opened once while anything is
 * listening and closed when the last listener leaves. The snapshot is the same
 * frozen object until the class or `short` changes, so a resize inside a class
 * re-renders nothing. Before anything listens (a first render, a test that
 * changes the window between renders) it reads the queries fresh.
 *
 * Without `matchMedia` (a server render) the window is expanded and not short,
 * which is what antd's breakpoints reported before they had measured.
 */

const SNAPSHOTS = new Map();

function snapshotOf(windowClass, short) {
	const key = `${windowClass}|${short ? 1 : 0}`;
	let snapshot = SNAPSHOTS.get(key);
	if (!snapshot) {
		snapshot = Object.freeze({
			windowClass,
			compact: windowClass === 'compact',
			medium: windowClass === 'medium',
			expanded: windowClass === 'expanded',
			short: Boolean(short),
		});
		SNAPSHOTS.set(key, snapshot);
	}
	return snapshot;
}

const SERVER_SNAPSHOT = snapshotOf('expanded', false);

const QUERY_KEYS = ['compact', 'expanded', 'short'];

let lists = null;
const listeners = new Set();

const canMatch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

function openLists() {
	const opened = {};
	for (const key of QUERY_KEYS) {
		try {
			opened[key] = window.matchMedia(WINDOW_MEDIA[key]);
		} catch (e) {
			opened[key] = null;
		}
	}
	return opened;
}

function readFrom(source) {
	const matches = key => Boolean(source[key] && source[key].matches);
	const windowClass = matches('compact') ? 'compact' : matches('expanded') ? 'expanded' : 'medium';
	return snapshotOf(windowClass, matches('short'));
}

/** The current class, outside React (an effect, an event handler). */
export function getWindowClass() {
	if (!canMatch()) return SERVER_SNAPSHOT;
	// While something listens the open lists are current (they fire `change`);
	// otherwise ask afresh, so nothing stale is kept between renders.
	return readFrom(lists || openLists());
}

function notify() {
	for (const listener of Array.from(listeners)) listener();
}

function listen(list, on) {
	if (!list) return;
	if (typeof list.addEventListener === 'function') {
		if (on) list.addEventListener('change', notify); else list.removeEventListener('change', notify);
	} else if (typeof list.addListener === 'function') {
		// Safari before 14.
		if (on) list.addListener(notify); else list.removeListener(notify);
	}
}

/**
 * Calls `listener` whenever the window may have changed class (or `short`).
 * Returns the unsubscribe.
 */
export function subscribeWindowClass(listener) {
	if (!canMatch()) return () => {};
	listeners.add(listener);
	if (!lists) {
		lists = openLists();
		for (const key of QUERY_KEYS) listen(lists[key], true);
	}
	return () => {
		listeners.delete(listener);
		if (!listeners.size && lists) {
			for (const key of QUERY_KEYS) listen(lists[key], false);
			lists = null;
		}
	};
}

const getServerSnapshot = () => SERVER_SNAPSHOT;

export default function useWindowClass() {
	return useSyncExternalStore(subscribeWindowClass, getWindowClass, getServerSnapshot);
}
